import Styles from 'src/components/Styles';
import {
  StyleSheet,
  View,
  Image,
  Dimensions,
  Platform,
  PixelRatio,
} from 'react-native';
import {
  PickupLocationStylesDescriptor,
  PickupTimeStylesDescriptor,
  ProductDetailsStylesDescriptor,
  MenuListStylesDescriptor,
  FlashListPropsType,
  Identifiable,
  ProductComponentProps,
} from 'src/components/screens/orderAhead/descriptor/DescriptorType';
import Localized from 'src/constants/AppStrings';
import {ensureHttps} from 'src/services/StringUtils';
import AVText from 'src/components/elements/AVText';
import {MenuProductType} from 'src/types/Menu';
import SearchProduct from 'src/components/elements/cart/SearchProduct';
import {DNA} from 'src/types/dnaTypes';
import {TransactionDetail} from 'src/types/TransactionDetail';
import {ListScanItemsProps} from 'src/components/screens/scanAndPay/ListScanItems';
import {
  DietaryHealthGood,
  DietaryHealthWarning,
  DietaryHealthAlert,
} from 'src/components/img/svg/DietaryHealthGrades';
import AVFormattedCurrency from 'src/components/elements/AVFormattedCurrency';
import TagLightIcon from 'src/components/img/svg/TagLightIcon';
import AccountStore from 'src/stores/AccountStore';

const width = Dimensions.get('window').width;

export function getPickupLocationStyles(
  _disabled?: boolean,
): PickupLocationStylesDescriptor {
  return StyleSheet.create({
    buttonText: {
      color: _disabled ? Styles.white : Styles.Colors.CompanyKitchen.black,
      fontSize: Styles.Fonts.f7,
      fontFamily: Styles.FontFamily.latoRegular,
      fontWeight: '400',
    },
    buttonContainerStyle: {
      position: 'absolute',
      bottom: 32,
      alignSelf: 'center',
    },
    errorText: {
      alignItems: 'center',
      fontWeight: '700',
      fontFamily: Styles.FontFamily.latoRegular,
      fontSize: Styles.Fonts.f7,
      color: Styles.dangerColor,
      paddingHorizontal: Styles.Spacing.m1,
      marginBottom: 1,
    },
    headerText: {
      color: Styles.darkColor,
      fontWeight: '700',
      fontFamily: Styles.FontFamily.latoRegular,
      marginBottom: Styles.Spacing.m1,
      fontSize: Styles.Fonts.f7,
    },
    errorContainer: {
      paddingVertical: Styles.Heights.h1,
      flexDirection: 'row',
      alignItems: 'center',
    },
  });
}

export function renderColorNutritionComponent(
  nutritionDisplayColor: string,
  nutritionDisplayLetter: string,
  nutritionCalorieTotal: number,
) {
  let healthGradeIcon: JSX.Element | false;
  if (
    nutritionDisplayLetter?.toUpperCase() === 'G' &&
    nutritionDisplayColor?.toUpperCase() === 'GREEN'
  ) {
    healthGradeIcon = (
      <View
        accessible={true}
        accessibilityLabel="Health grade: good, "
        accessibilityRole="image"
        role="img"
        aria-label="Health grade: good, "
      >
        <DietaryHealthGood />
      </View>
    );
  } else if (
    nutritionDisplayLetter?.toUpperCase() === 'Y' &&
    nutritionDisplayColor?.toUpperCase() === 'YELLOW'
  ) {
    healthGradeIcon = (
      <View
        accessible={true}
        accessibilityLabel="Health grade: warning, "
        accessibilityRole="image"
        role="img"
        aria-label="Health grade: warning, "
      >
        <DietaryHealthWarning />
      </View>
    );
  } else if (
    nutritionDisplayLetter?.toUpperCase() === 'R' &&
    nutritionDisplayColor?.toUpperCase() === 'RED'
  ) {
    healthGradeIcon = (
      <View
        accessible={true}
        accessibilityLabel="Health grade: alert, "
        accessibilityRole="image"
        role="img"
        aria-label="Health grade: alert, "
      >
        <DietaryHealthAlert />
      </View>
    );
  }

  return (
    <View>
      {nutritionCalorieTotal > 0 && (
        <View>
          <View
            style={{
              flex: 1,
              flexDirection: 'row',
              justifyContent: 'flex-start',
              alignItems: 'center',
              marginLeft: -Styles.Spacing.m1,
              marginTop: Styles.Spacing.m3,
              marginBottom: Styles.Spacing.m2,
            }}
          >
            <View style={{padding: Styles.Spacing.m1}}>{healthGradeIcon}</View>
            <View
              style={{
                alignItems: 'flex-start',
              }}
            >
              <AVText
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
                style={{
                  marginBottom: Styles.Spacing.m1,
                  color: '#22212D',
                  fontSize: 18,
                  fontFamily: Styles.FontFamily.latoRegular,
                  fontWeight: 'bold',
                  alignItems: 'flex-start',
                  paddingLeft: Styles.Spacing.m1,
                }}
              >
                {nutritionCalorieTotal}
              </AVText>
            </View>
            <View style={Styles.Style.labelColumn2}>
              <AVText
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
                style={{
                  marginBottom: Styles.Spacing.m1,
                  color: '#22212D',
                  fontSize: 18,
                  fontFamily: Styles.FontFamily.latoRegular,
                  fontWeight: 'bold',
                  alignItems: 'flex-start',
                  paddingLeft: Styles.Spacing.m1,
                }}
              >
                {this.props?.location?.locationFeatures?.dna
                  ?.showCaloriesAsKilocalories
                  ? Localized.Labels.total_kilocalories
                  : Localized.Labels.total_calories}
              </AVText>
            </View>
          </View>
        </View>
      )}
    </View>
  );
}

export function renderNutritionPromotionComponent(
  nutritionPromoName: string,
  nutritionDiscountAmount: number,
) {
  return (
    !isNaN(nutritionDiscountAmount) &&
    nutritionPromoName !== undefined &&
    nutritionPromoName !== '' && (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'flex-end',
          width: '100%',
          marginTop: Styles.Spacing.m2,
        }}
      >
        <View>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              marginRight: Styles.Spacing.m3,
            }}
          >
            <TagLightIcon
              fill={Styles.Colors.Canteen.green}
              style={{marginRight: Styles.Spacing.m2}}
            />
            <AVText
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
              style={{
                color: Styles.Colors.Canteen.green,
                fontFamily: Styles.FontFamily.latoRegular,
                fontSize: Styles.Fonts.f7,
              }}
            >
              {nutritionPromoName}
            </AVText>
          </View>
        </View>
        <View>
          <AVFormattedCurrency
            currency={AccountStore.getCurrency()}
            value={nutritionDiscountAmount * -1}
            style={{
              color: Styles.Colors.Canteen.green,
              fontFamily: Styles.FontFamily.latoRegular,
              fontSize: Styles.Fonts.f7,
            }}
            accessibilityLabel={`${Localized.Labels.discounts}, $${(
              nutritionDiscountAmount * -1
            ).toString()}`}
            accessibilityRole="text"
            aria-label={`${Localized.Labels.discounts}, $${(
              nutritionDiscountAmount * -1
            ).toString()}, text`}
          />
        </View>
      </View>
    )
  );
}

export function getInactiveButtonColor() {
  return Styles.Colors.CompanyKitchen.lightGray;
}

export function textColor() {
  return Styles.darkColor;
}

export function fontFamilyStyle() {
  return Styles.FontFamily.latoRegular;
}

export function getPickupTimeStyles(): PickupTimeStylesDescriptor {
  return StyleSheet.create({
    dateView: {
      marginLeft: Styles.Spacing.m3,
      marginTop: Styles.Spacing.m4 - Styles.Spacing.m2,
    },
    timeView: {
      marginTop: Styles.Spacing.m4 - Styles.Spacing.m2,
    },
    pickupTimeContainer: {
      backgroundColor: Styles.white,
      flex: 1,
      marginTop: Styles.Heights.h2,
    },
    pickupLocationHeaderView: {
      justifyContent: 'space-between',
      alignItems: 'center',
      flexDirection: 'row',
      minHeight: Styles.Heights.touchTargetHeight2,
      width: '100%',
      marginLeft: Styles.Spacing.m3,
      paddingRight: Styles.Heights.h1,
    },
    tilesStyle: {
      marginTop: Styles.Spacing.m3,
      paddingRight: Styles.Spacing.m3,
    },
    customTileButtonTime: {
      minWidth: 105,
    },
    errorContainer: {
      flexDirection: 'row',
      marginTop: Styles.Spacing.m2,
      alignItems: 'center',
    },
    editButton: {
      flexDirection: 'row',
      alignItems: 'center',
      borderWidth: 1,
      borderColor: Styles.Colors.CompanyKitchen.black,
      borderRadius: 32,
      paddingHorizontal: 8,
      paddingVertical: 5,
      marginRight: Styles.Spacing.m3,
      overflow: 'hidden',
    },
    buttonTextStyle: {
      fontWeight: '400',
      paddingHorizontal: Styles.Spacing.m1,
      fontSize: Styles.Fonts.f1,
      color: Styles.Colors.CompanyKitchen.black,
      marginBottom: 1,
      fontFamily: Styles.FontFamily.latoRegular,
    },
  });
}
export function timeErrorMessage() {
  return Localized.Errors.error_please_select_a_time;
}
export function getActiveButtonColor() {
  return Styles.Colors.CompanyKitchen.green;
}
export function productDetailsTitle() {
  return Localized.Labels.item_info;
}
export function locationErrorMessage() {
  return Localized.Errors.please_select_pickup_location;
}
export function dayErrorMessage() {
  return Localized.Errors.error_please_select_a_day;
}

export function productDetailsImageView(imageUri: string): JSX.Element {
  return (
    <>
      {imageUri && (
        <View style={{width: width}}>
          <Image
            accessible={true}
            role="img"
            accessibilityRole="image"
            aria-label="Product image"
            accessibilityLabel="Product image"
            resizeMode={'cover'}
            style={{aspectRatio: 1.3}}
            source={{uri: ensureHttps(imageUri)}}
          />
        </View>
      )}
    </>
  );
}

export function productDetailsStyles(): ProductDetailsStylesDescriptor {
  return StyleSheet.create({
    content: {
      marginVertical: Styles.Spacing.m1,
      paddingVertical: Styles.Spacing.m2,
    },
    container: {
      flex: 1,
      backgroundColor: Styles.white,
      marginVertical: 0,
      marginHorizontal: 0,
      paddingHorizontal: Styles.Spacing.m2,
    },
    infoContainer: {
      paddingVertical: 10,
      paddingHorizontal: Styles.Spacing.m3,
      flexDirection: 'column',
    },
    title: {
      fontSize: Styles.Fonts.f3,
      fontWeight: '600',
      marginBottom: Styles.Spacing.m2,
      color: Styles.darkColor,
    },
    productPrice: {
      marginTop: 1,
      fontWeight: '600',
      color: Styles.darkColor,
      fontSize: Styles.Fonts.f2,
    },
    calories: {
      fontSize: Styles.Fonts.f2,
      fontWeight: '600',
      color: Styles.darkColor,
    },
    calorieText: {
      fontSize: 11,
      color: Styles.darkColor,
      fontWeight: '400',
      marginTop: 7.5,
      marginLeft: 5,
    },
    verticalLine: {
      marginHorizontal: 8,
      backgroundColor: Styles.lightGray,
      borderLeftWidth: 1,
    },
    healthGrade: {
      marginLeft: 10,
      paddingTop: 5,
    },

    nutritionButtonView: {
      flexDirection: 'row',
      marginVertical: Styles.Spacing.m2,
    },
    outlined: {
      justifyContent: 'center',
      width: Platform.OS !== 'web' ? width / 2 : width / 5,
      flexDirection: 'row',
      borderColor: Styles.Colors.CompanyKitchen.green,
      borderWidth: 2,
      borderRadius: 60,
      overflow: 'hidden',
      padding: Styles.Spacing.m2,
      marginTop: Styles.Spacing.m2,
      backgroundColor: Styles.Colors.CompanyKitchen.green,
    },
    outlinedButtonText: {
      color: Styles.Colors.CompanyKitchen.black,
      fontSize: Styles.Fonts.f1,
      fontWeight: '700',
    },
    descriptionContainer: {
      marginHorizontal: 17.5,
    },
    description: {
      color: Styles.lightGray,
      fontSize: Styles.Fonts.f1,
      marginTop: Styles.Spacing.m2,
    },
    customizationSection: {
      marginTop: 15,
      marginHorizontal: 17.5,
      flexDirection: 'column',
      flex: 1,
    },
    quantityContainer: {
      alignItems: 'center',
    },
    quantitySelectionView: {
      padding: 0,
      justifyContent: 'center', // Centers vertically
      flex: 1, // Takes up all available space
      alignItems: 'center',
    },
    dashStyle: {
      borderRadius: 100,
      marginVertical: Styles.Spacing.m3,
      height: PixelRatio.roundToNearestPixel(2),
      overflow: 'hidden',
      width: '100%',
    },
    quantityLabel: {
      fontSize: Styles.Fonts.f1,
      color: Styles.darkColor,
      marginBottom: Styles.Spacing.m1 * -1,
    },
  });
}

export function roundedButtonColor() {
  return Styles.Colors.CompanyKitchen.green;
}
export function dashLineColor() {
  return '#999';
}

export function menuListStyles(): MenuListStylesDescriptor {
  return StyleSheet.create({
    content: {
      flex: 1,
      backgroundColor: Styles.white,
    },
    searchContainer: {
      marginHorizontal: Styles.Spacing.m2,
    },
    categories: {
      marginVertical: Styles.Spacing.m2,
    },
  });
}
export function getFlashListProps<T extends Identifiable>({
  data,
  renderItem,
}: FlashListPropsType<T>) {
  return {
    data,
    renderItem,
    keyExtractor: (item: T) => item.id,
    ListEmptyComponent: (
      <View style={{justifyContent: 'center', alignItems: 'center'}}>
        <AVText style={{fontSize: Styles.Fonts.f2}}>
          {Localized.Labels.no_products_available}
        </AVText>
      </View>
    ),
    estimatedItemSize: 72,
  };
}

export function getProductComponent(props: ProductComponentProps) {
  return (
    <SearchProduct
      name={props?.name}
      dnaConfiguration={props?.dnaConfiguration}
      onPress={props?.onPress}
      isProductCat={props?.isProductCat}
      price={props?.price}
      imageUrl={props?.imageUrl}
      item={props?.item}
    />
  );
}

export function getTransactionUpsellName(_name: string) {
  return null;
}

export function getTransactionUpsellProductsList(
  _products: MenuProductType[],
  _addItem: (item: MenuProductType) => void,
  _dna?: DNA,
) {
  return null;
}

export function getEditLocationIconColor() {
  return Styles.Colors.CompanyKitchen.black;
}
export function getEmptyCartlblStyle() {
  return {};
}
export function activeCalendarIconColor() {
  return Styles.Colors.CompanyKitchen.black;
}

export function inActiveCalendarIconColor() {
  return Styles.Colors.CompanyKitchen.black;
}

export function renderTenderDiscount(
  _transaction: TransactionDetail | null,
  _props: ListScanItemsProps,
) {
  return null;
}

export default {
  textColor,
  fontFamilyStyle,
  getPickupTimeStyles,
  productDetailsImageView,
  productDetailsStyles,
  roundedButtonColor,
  dashLineColor,
  menuListStyles,
  getPickupLocationStyles,
  getInactiveButtonColor,
  getFlashListProps,
  getActiveButtonColor,
  locationErrorMessage,
  dayErrorMessage,
  timeErrorMessage,
  productDetailsTitle,
  getProductComponent,
  getEmptyCartlblStyle,
  getTransactionUpsellName,
  getTransactionUpsellProductsList,
  getEditLocationIconColor,
  activeCalendarIconColor,
  inActiveCalendarIconColor,
  renderTenderDiscount,
  renderColorNutritionComponent,
  renderNutritionPromotionComponent,
};
